import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import * as XLSX from "xlsx";
import { RESULT_HEADER_MAP } from "./driveInAutoData";

export interface HeaderMapItem {
  label: string;
  key: string;
}

export interface DriveInResultItem {
  profileId: string | number;
  warnings?: string[];
  technical?: Record<string, any>;
  result?: Record<string, any>;
  [key: string]: any;
}

export interface DriveInExcelPayload {
  constantInputs: Record<string, any>;
  top3Results: DriveInResultItem[];
  allResults: DriveInResultItem[];
  profileNameLookup: Record<string | number, string>;
}

export async function generateExcel(
  payload: DriveInExcelPayload = {
    constantInputs: {},
    top3Results: [],
    allResults: [],
    profileNameLookup: {},
  },
  filename: string = "drive_in_auto_results.xlsx",
): Promise<void> {
  try {
    const { constantInputs, top3Results, allResults, profileNameLookup } =
      payload;
    const wb = XLSX.utils.book_new();

    const generateResultSheet = (
      dataArray: DriveInResultItem[],
      sheetName: string,
      includeTechnical: boolean = false,
    ): void => {
      const sheetData: any[][] = [];
      const headers = (RESULT_HEADER_MAP as HeaderMapItem[]).map(
        (h) => h.label,
      );

      let finalHeaders = [...headers];

      if (includeTechnical) {
        finalHeaders.push("Warnings");
        const technicalKeys = new Set<string>();
        dataArray.forEach((item) => {
          if (item.technical) {
            Object.keys(item.technical).forEach((key) =>
              technicalKeys.add(key),
            );
          }
        });
        technicalKeys.forEach((key) => finalHeaders.push(`Technical: ${key}`));
      }

      sheetData.push(finalHeaders);

      for (const item of dataArray) {
        const profileName =
          profileNameLookup[item.profileId] || `ID ${item.profileId}`;

        const mappedRow = (RESULT_HEADER_MAP as HeaderMapItem[]).map(
          (header) => {
            if (header.key === "profileName") return profileName;

            const value = item[header.key] ?? item.result?.[header.key];
            return value === undefined || value === null ? "" : String(value);
          },
        );

        if (includeTechnical) {
          const warnings = item.warnings ? item.warnings.join("; ") : "";
          mappedRow.push(warnings);

          const technicalData = item.technical || {};
          finalHeaders.slice(headers.length + 1).forEach((headerLabel) => {
            const techKey = headerLabel.replace("Technical: ", "");
            let value = technicalData[techKey];

            if (Array.isArray(value)) {
              value = `[Array: ${value.length} items]`;
            }

            mappedRow.push(
              value === undefined || value === null ? "" : String(value),
            );
          });
        }

        sheetData.push(mappedRow);
      }

      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      const colWidths = finalHeaders.map(() => ({ wch: 15 }));
      colWidths[0].wch = 25;
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    };

    const inputsSheet: any[][] = [];
    for (const [label, value] of Object.entries(constantInputs)) {
      inputsSheet.push([
        label,
        value === undefined || value === null ? "" : String(value),
      ]);
    }

    const wsInputs = XLSX.utils.aoa_to_sheet(inputsSheet);
    wsInputs["!cols"] = [{ wch: 35 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, wsInputs, "Inputs");

    generateResultSheet(top3Results, "Top 3 Uprights", true);
    // generateResultSheet(allResults, "All uprights", true);

    if (Capacitor.isNativePlatform()) {
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "base64" });

      const uniqueFileName = `${Date.now()}_${filename}`;
      const savedFile = await Filesystem.writeFile({
        path: uniqueFileName,
        data: wbout,
        directory: Directory.Cache,
      });

      await Share.share({
        title: "Export Excel",
        text: "Sharing your results",
        url: savedFile.uri,
        dialogTitle: "Share Excel File",
      });
    } else {
      XLSX.writeFile(wb, filename);
    }
  } catch (err) {
    throw err;
  }
}
